import { CKSelect } from '@clearkit/react';
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';
import { useState } from 'react';

import ChipInput from '~/components/ChipInput';
import InfoTooltip from '~/components/InfoTooltip';
import MultiPicklist from '~/components/MultiPicklist';
import TextField from '~/components/TextField';
import ToastsContainer from '~/containers/Toasts';
import EmailIcon from '~/images/destinations/Email.svg';
import { destinationTypeEmail } from '~/lib/destinations';
import { emailRegex } from '~/lib/email';
import { hasError } from '~/lib/graphql';
import {
  legacySegmentType,
  readableSegmentType,
  readableSingularSegmentType,
} from '~/lib/segments';
import DeliverTestButton from '~/pages/destinations/components/DeliverTestButton';
import SuppressionForm from '~/pages/destinations/components/SuppressionForm';
import TriggerType from '~/pages/destinations/components/TriggerType';
import defaultTriggerTypes from '~/pages/destinations/components/TriggerType/defaultValues';

import ContentBox from '../../components/ContentBox';
import FormContentWrapper from '../../components/FormContentWrapper';
import FormHeader from '../../components/FormHeader';
import sharedStyles from '../../New/styles';
import {
  getDeliveryTargets,
  HUBSPOT_TARGETS,
  SALESFORCE_TARGETS,
} from '../dynamicDeliveryTargets';
import EmailDestinationDescription from './DestinationDescription';
import styles from './styles';

function hourMenuItems() {
  let hourMenuItems = [];
  for (let i = 1; i <= 12; i++) {
    hourMenuItems.push(
      <MenuItem key={i} value={i}>
        {i}
      </MenuItem>,
    );
  }
  return hourMenuItems;
}

function EmailDestination({
  classes,
  errors,
  segment,
  attributes,
  destination,
  accountConnectors,
  FormFooter,
}) {
  let deliveryTargets = [
    {
      value: 'email',
      label: 'Email addresses',
      icon: <EmailIcon className="w-4 h-4" />,
    },
  ];

  if (accountConnectors.salesforce) {
    deliveryTargets = [
      ...deliveryTargets,
      ...getDeliveryTargets(SALESFORCE_TARGETS, segment.type),
    ];
  }
  if (accountConnectors.hubspot) {
    deliveryTargets = [
      ...deliveryTargets,
      ...getDeliveryTargets(HUBSPOT_TARGETS, segment.type),
    ];
  }

  const isNew = !destination;
  const initialConfig = isNew ? {} : destination.config;
  const initialState = {
    columns: initialConfig.columns || [],
    duplicateSuppressionTimeframe:
      initialConfig.duplicateSuppressionTimeframe || 0,
    frequency: initialConfig.frequency || 'hourly',
    scheduleDay: initialConfig.scheduleDay || 0,
    scheduleHour: initialConfig.scheduleHour || 9,
    scheduleStartOfPeriod: initialConfig.scheduleStartOfPeriod || null,
    recipients: initialConfig.recipients || [],
    subject: initialConfig.subject || null,
    deliveryTarget: initialConfig.deliveryTarget || 'email',
    triggerType: isNew ? 'segment_enter' : destination.triggerType,
  };

  const [
    duplicateSuppressionTimeframe,
    setDuplicateSuppressionTimeframe,
  ] = useState(initialState.duplicateSuppressionTimeframe);
  const [frequency, setFrequency] = useState(initialState.frequency);
  const [recipients, setRecipients] = useState(initialState.recipients);
  const [subject, setSubject] = useState(initialState.subject);
  const [triggerType, setTriggerType] = useState(initialState.triggerType);
  const [columns, setColumns] = useState(initialState.columns);
  const [scheduleDay, setScheduleDay] = useState(initialState.scheduleDay);
  const [scheduleHour, setScheduleHour] = useState(initialState.scheduleHour);
  const [scheduleStartOfPeriod, setScheduleStartOfPeriod] = useState(
    initialState.scheduleStartOfPeriod,
  );

  const [deliveryTarget, setDeliveryTarget] = useState(
    initialState.deliveryTarget,
  );

  const handleAmPmChange = (event) => {
    const { value } = event.target;

    handleHourAndAmPmChange(scheduleHour, value);
  };

  const handleHourAndAmPmChange = (hour, amPm) => {
    if (amPm === 'pm' && hour === 12) {
      // midday
      hour = 12;
    } else if (amPm === 'am' && hour === 12) {
      // midnight
      hour = 0;
    } else if (amPm === 'pm' && hour < 12) {
      // afternoon
      hour += 12;
    } else if (amPm === 'am' && hour > 12) {
      // morning
      hour -= 12;
    }

    setScheduleHour(hour);
  };

  const handleHourChange = (event) => {
    let { value } = event.target;

    let amPm;
    if (scheduleHour === 0) {
      amPm = 'am';
    } else if (scheduleHour === 12) {
      amPm = 'pm';
    } else if (scheduleHour < 12) {
      amPm = 'am';
    } else if (scheduleHour > 12) {
      amPm = 'pm';
    }

    handleHourAndAmPmChange(value, amPm);
  };

  const destinationConfig = () => {
    const { timeZone } = Intl.DateTimeFormat().resolvedOptions();

    return {
      config: {
        columns,
        duplicateSuppressionTimeframe,
        frequency,
        scheduleDay: scheduleDay || 0,
        scheduleHour: scheduleHour || 9,
        scheduleStartOfPeriod,
        recipients,
        deliveryTarget,
        subject,
        timeZone,
      },
      triggerType,
    };
  };

  const humanizedSegmentType = readableSingularSegmentType(segment.type);
  const destinationFlowDescription = destination ? (
    <EmailDestinationDescription config={destination.config} />
  ) : null;

  const selectedDeliveryTarget = deliveryTargets.find(
    (t) => t.value === deliveryTarget,
  );

  return (
    <>
      <FormHeader
        destinationType={destinationTypeEmail}
        destinationName="Email Digest"
        destinationFlowDescription={destinationFlowDescription}
        segmentName={segment.name}
        segmentCollection={segment.collection.name}
      />
      <FormContentWrapper>
        <h2 className={classes.subheading}>Trigger</h2>
        <span className={classes.triggerSubtext}>
          Select the event that triggers this Destination to run.{' '}
          <a
            href="https://help.clearbit.com/hc/en-us/articles/360023453694"
            target="_blank"
            rel="noopener noreferrer"
            style={{ textDecoration: 'underline' }}>
            Learn more
          </a>
        </span>

        <TriggerType
          errors={errors}
          handleChange={(_, value) => {
            setTriggerType(value);
          }}
          triggerType={triggerType}
          triggerTypes={defaultTriggerTypes(segment.type)}
        />

        <h2 className={classes.subheading}>Configure email options</h2>

        <ContentBox>
          <FormControl fullWidth variant="standard">
            <InputLabel required>Recipients</InputLabel>
            <>
              <div className="flex">
                <div className="w-1/3 mt-2 mb-4 mr-2">
                  <CKSelect
                    placeholder="Select an option"
                    itemToString={(item) => item.label}
                    items={deliveryTargets}
                    isDisabled={deliveryTargets.length === 1}
                    leftIcon={selectedDeliveryTarget.icon}
                    selectedItem={selectedDeliveryTarget}
                    onSelectedItemChange={(changes) => {
                      setDeliveryTarget(changes.selectedItem.value);
                    }}
                  />
                </div>
                <div className="w-2/3">
                  <EmailInput
                    className="mt-2 mb-4"
                    classes={classes}
                    errors={errors}
                    segment={segment}
                    recipients={recipients}
                    setRecipients={setRecipients}
                    required={false}
                    placeholder={
                      deliveryTarget === 'email'
                        ? 'Enter email addresses'
                        : 'Enter fallback email addresses'
                    }
                  />
                </div>
              </div>
              {deliveryTarget === 'email' ? null : (
                <p className="mb-4 text-sm">
                  Send and Email Digest dynamically to the assigned Owner within
                  your CRM. You can set one or more fallback email addresses in
                  case there is no defined owner.
                </p>
              )}
            </>
          </FormControl>

          <TextField
            className={classes.field}
            error={hasError(errors, 'subject')}
            defaultValue={subject}
            fullWidth
            InputProps={{
              placeholder: `New ${readableSegmentType(segment.type)} matched`,
            }}
            label="Email subject"
            onChange={(event) => {
              setSubject(event.target.value);
            }}
            required
            variant="standard"
          />

          <hr className={classes.break} />

          <SuppressionForm
            typename={humanizedSegmentType}
            exclusion_timeframe={duplicateSuppressionTimeframe}
            onChange={({ exclusion_timeframe }) => {
              setDuplicateSuppressionTimeframe(exclusion_timeframe);
            }}
          />

          <hr className={classes.break} />

          <DeliverySchedule
            {...{
              classes,
              handleAmPmChange,
              handleHourChange,
              frequency,
              setFrequency,
              scheduleDay,
              setScheduleDay,
              scheduleHour,
              scheduleStartOfPeriod,
              setScheduleStartOfPeriod,
            }}
          />
        </ContentBox>
        <CustomizeAttributes
          {...{
            attributes,
            columns,
            setColumns,
            classes,
            segment,
          }}
        />

        <EmailTest
          {...{
            classes,
            segment,
            columns,
            duplicateSuppressionTimeframe,
            recipients,
            frequency,
            subject,
          }}
        />
      </FormContentWrapper>

      <FormFooter destinationConfig={destinationConfig()} />
    </>
  );
}

function CustomizeAttributes({
  attributes,
  columns,
  setColumns,
  classes,
  segment,
}) {
  return (
    <>
      <h2 className={classes.subheading}>Customize your CSV</h2>

      <p className={classes.columnsHelperText}>
        Select which attributes you want to include as columns in your CSV
      </p>

      <MultiPicklist
        hubspotComingSoon
        allColumns={attributes}
        onChange={(value) => {
          setColumns(value);
        }}
        type={legacySegmentType(segment.type)}
        value={columns}
      />
    </>
  );
}

function EmailInput({
  classes,
  errors,
  recipients,
  setRecipients,
  placeholder,
  required = true,
  errorKey = 'recipients',
}) {
  function addRecipient(newRecipient) {
    if (newRecipient === '') {
      return;
    }

    if (!newRecipient.match(emailRegex)) {
      ToastsContainer.addWarning(
        'That email address is not valid. Please try again.',
      );
      return;
    }

    if (newRecipient) {
      setRecipients([...recipients, newRecipient]);
    }
  }
  function removeRecipient(oldRecipient) {
    setRecipients(recipients.filter((recipient) => recipient !== oldRecipient));
  }

  return (
    <>
      <ChipInput
        error={hasError(errors, errorKey)}
        fullWidth
        helperText={
          hasError(errors, errorKey) && 'Please enter a valid email address.'
        }
        InputProps={{
          style: {
            minWidth: 180,
          },
          placeholder:
            recipients.length == 0 ? placeholder : 'Add more recipients',
        }}
        classes={{
          root: classes.chipInput,
          chipContainer: classes.chipContainer,
        }}
        onAdd={addRecipient}
        onBlur={(e) => addRecipient(e.target.value)}
        onDelete={removeRecipient}
        placeholder="Type emails here..."
        required={required}
        value={recipients}
      />
    </>
  );
}

function DeliverySchedule({
  classes,
  handleAmPmChange,
  handleHourChange,
  frequency,
  setFrequency,
  scheduleDay,
  setScheduleDay,
  scheduleHour,
  scheduleStartOfPeriod,
  setScheduleStartOfPeriod,
}) {
  return (
    <>
      <InputLabel className="flex items-center">
        Schedule email digest
        <InfoTooltip
          unstable_portal={false}
          title="Select how often the email digest should be sent."
        />
      </InputLabel>

      <div className={classes.scheduleField}>
        <Select
          className={classes.frequency}
          value={frequency}
          onChange={(event) => {
            setFrequency(event.target.value);
          }}>
          <MenuItem value="hourly">Hourly</MenuItem>
          <MenuItem value="daily">Daily</MenuItem>
          <MenuItem value="weekly">Weekly</MenuItem>
          <MenuItem value="monthly">Monthly</MenuItem>
        </Select>
        {frequency === 'monthly' && (
          <>
            on the
            <Select
              className={classes.scheduleDayOrder}
              value={scheduleStartOfPeriod}
              onChange={(event) => {
                setScheduleStartOfPeriod(event.target.value);
              }}>
              <MenuItem value>first</MenuItem>
              <MenuItem value={false}>last</MenuItem>
            </Select>{' '}
            <Select
              className={classes.scheduleDay}
              value={scheduleDay || 0}
              onChange={(event) => {
                setScheduleDay(event.target.value);
              }}>
              <MenuItem value={0}>Sunday</MenuItem>
              <MenuItem value={1}>Monday</MenuItem>
              <MenuItem value={2}>Tuesday</MenuItem>
              <MenuItem value={3}>Wednesday</MenuItem>
              <MenuItem value={4}>Thursday</MenuItem>
              <MenuItem value={5}>Friday</MenuItem>
              <MenuItem value={6}>Saturday</MenuItem>
            </Select>
            of the month{' '}
          </>
        )}
        {frequency === 'weekly' && (
          <>
            on
            <Select
              className={classes.scheduleDay}
              value={scheduleDay || 0}
              onChange={(event) => {
                setScheduleDay(event.target.value);
              }}>
              <MenuItem value={0}>Sunday</MenuItem>
              <MenuItem value={1}>Monday</MenuItem>
              <MenuItem value={2}>Tuesday</MenuItem>
              <MenuItem value={3}>Wednesday</MenuItem>
              <MenuItem value={4}>Thursday</MenuItem>
              <MenuItem value={5}>Friday</MenuItem>
              <MenuItem value={6}>Saturday</MenuItem>
            </Select>
          </>
        )}

        {frequency !== 'hourly' && (
          <>
            at
            <Select
              className={classes.scheduleHour}
              classes={{ root: 'hourSelect' }}
              value={scheduleHour === 0 ? 12 : scheduleHour}
              onChange={handleHourChange}>
              {hourMenuItems()}
            </Select>
            <Select
              className={classes.scheduleAmPm}
              classes={{ root: 'amPmSelect' }}
              value={scheduleHour >= 12 ? 'pm' : 'am'}
              onChange={handleAmPmChange}>
              <MenuItem value="am">am</MenuItem>
              <MenuItem value="pm">pm</MenuItem>
            </Select>
          </>
        )}
      </div>
    </>
  );
}

function EmailTest({
  classes,
  segment,
  columns,
  duplicateSuppressionTimeframe,
  recipients,
  frequency,
  subject,
}) {
  let [testRecipients, setTestRecipients] = useState(
    recipients[0] ? [recipients[0]] : [],
  );

  return (
    <>
      <h2 className={classes.subheading}>Test this destination</h2>
      <span className={classes.triggerSubtext}>
        Run a test by manually triggering this destination
      </span>
      <ContentBox>
        <DeliverTestButton
          config={{
            columns,
            duplicateSuppressionTimeframe,
            frequency,
            recipients: testRecipients,
            subject,
          }}
          label="email"
          destinationType="email"
          segmentId={segment.id}
          segmentType={segment.type}>
          <TextField
            label="Test email recipient"
            FormControlProps={{
              classes: {
                root: classes.testRecipientTextField,
              },
            }}
            onChange={(event) => setTestRecipients([event.target.value])}
            value={testRecipients}
            variant="outlined"
          />
        </DeliverTestButton>
      </ContentBox>
    </>
  );
}

EmailDestination.propTypes = {
  accountConnectors: PropTypes.shape({
    salesforce: PropTypes.object,
    hubspot: PropTypes.object,
  }),
  attributes: PropTypes.array.isRequired, //TODO: arrayOf
  classes: PropTypes.object.isRequired,
  destination: PropTypes.object,
  errors: PropTypes.array.isRequired,
  segment: PropTypes.object.isRequired,
  FormFooter: PropTypes.elementType.isRequired,
};

export default withStyles(
  (theme) => ({
    ...sharedStyles(theme),
    ...styles(theme),
  }),
  { name: 'SegmentDestinationNewEmail' },
)(EmailDestination);
